/**
 * Pure rules of the S5 invoice detail (SIH-17 I-8c, TL A3): where Back goes and
 * what the invoice's status reads as. No imports above the domain layer.
 */

import type { InvoiceRow, SyncStatus, ViewState } from './types'
import { branchPath, failedListPath, pendingListPath } from './tpinPaths'

/** The invoice the branch's list holds for this record number, or null when the period's list does not hold it. */
export interface InvoiceDetail {
  readonly row: InvoiceRow | null
}

/** What the S5 body shows: exactly one of these at a time, never blank. */
export type DetailBody =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'not-found' }
  | { readonly kind: 'found'; readonly row: InvoiceRow }

/**
 * The body for a view of the read. A refresh keeps showing what was on screen
 * (found or not found) under its refreshing marker; idle and loading both read
 * as loading, so there is no moment with nothing to say.
 */
export function detailBody(view: ViewState<InvoiceDetail>): DetailBody {
  switch (view.kind) {
    case 'error':
      return { kind: 'error', message: view.message }
    case 'ready':
      return view.data.row === null ? { kind: 'not-found' } : { kind: 'found', row: view.data.row }
    case 'refreshing':
      return view.previous.row === null ? { kind: 'not-found' } : { kind: 'found', row: view.previous.row }
    default:
      return { kind: 'loading' }
  }
}

/** The status words as the source delivered them, capitalised for display (BR-1: never recomputed). */
export const STATUS_WORD: Readonly<Record<SyncStatus, string>> = {
  FAILED: 'Failed',
  PENDING: 'Pending',
  SYNCED: 'Synced',
}

export interface BackTarget {
  /** Which worklist the invoice came from; null when the invoice is in neither (Back goes to the branch). */
  readonly kind: 'failed' | 'pending' | null
  readonly label: string
  readonly path: string
  /** The list's breadcrumb label, or null when there is no list level. */
  readonly crumb: string | null
}

/** The breadcrumb level for the list the invoice belongs to: one level, or none when the invoice is in neither list. */
export function listCrumb(back: BackTarget): readonly { readonly label: string; readonly to: string }[] {
  return back.crumb === null ? [] : [{ label: back.crumb, to: back.path }]
}

/**
 * Back to the worklist that holds the invoice: failed to S3, pending to S4, and
 * anything else (or an invoice not loaded yet) to its branch.
 */
export function backTarget(row: InvoiceRow | null, tin: string, dbName: string): BackTarget {
  const status = row === null ? null : row.derivedStatus
  if (status === 'FAILED') {
    return { kind: 'failed', label: 'Back to failed invoices', path: failedListPath(dbName), crumb: 'Failed invoices' }
  }
  if (status === 'PENDING') {
    return { kind: 'pending', label: 'Back to pending invoices', path: pendingListPath(dbName), crumb: 'Pending invoices' }
  }
  return { kind: null, label: 'Back to branch', path: branchPath(tin, dbName), crumb: null }
}
